import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import './Admin.css';

export default function RiderEarnings() {
  const { api } = useContext(AuthContext);

  const today = new Date().toISOString().split('T')[0];
  const monthStart = today.slice(0, 7) + '-01';

  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    from: monthStart,
    to: today,
    rider_id: ''
  });

  // ✅ useRef so fetchEarnings always reads latest filters — no stale closure
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchEarnings = async (overrideFilters) => {
    const active = overrideFilters || filtersRef.current;
    try {
      setError('');
      const params = {};
      if (active.from)     params.from     = active.from;
      if (active.to)       params.to       = active.to;
      if (active.rider_id) params.rider_id = active.rider_id;

      console.log('📡 Fetching rider earnings:', params);
      const response = await api.get('/orders/rider-earnings', { params });
      console.log('✅ Response:', response.data);

      const data = Array.isArray(response.data) ? response.data : [];
      setEarnings(data);
    } catch (err) {
      console.error('❌ Fetch earnings error:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to load earnings');
      setEarnings([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchEarnings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilter = () => {
    setLoading(true);
    setRefreshing(true);
    fetchEarnings(filters);
  };

  const handleClearFilter = () => {
    const reset = { from: monthStart, to: today, rider_id: '' };
    setFilters(reset);
    setLoading(true);
    fetchEarnings(reset);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchEarnings(filters);
  };

  const totalEarnings = earnings.reduce((sum, r) => sum + Number(r.total_earnings || 0), 0);
  const totalOrders   = earnings.reduce((sum, r) => sum + Number(r.total_orders   || 0), 0);

  if (loading) return <LoadingSpinner message="Loading earnings..." />;

  return (
    <div className="admin-product-management">
      <div className="container">

        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">
              <span className="title-icon">💰</span>
              Rider Earnings
            </h1>
            <p className="page-subtitle">Track earnings of each rider</p>
          </div>
          <div className="header-actions">
            <button
              className="btn btn-outline btn-sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
            </button>
          </div>
        </div>

        {/* Date Filter */}
        <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
          <h3 className="card-title" style={{ marginBottom: '15px' }}>🗓️ Filter by Date</h3>
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>From Date</label>
              <input
                type="date"
                name="from"
                value={filters.from}
                onChange={handleFilterChange}
                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd', display: 'block' }}
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>To Date</label>
              <input
                type="date"
                name="to"
                value={filters.to}
                onChange={handleFilterChange}
                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd', display: 'block' }}
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Rider ID (optional)</label>
              <input
                type="number"
                name="rider_id"
                value={filters.rider_id}
                onChange={handleFilterChange}
                placeholder="All riders"
                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd', width: '130px', display: 'block' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button className="btn btn-primary" onClick={handleApplyFilter}>
                🔍 Apply Filter
              </button>
              <button className="btn btn-secondary" onClick={handleClearFilter}>
                ✕ Reset
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <div className="card" style={{ flex: 1, minWidth: '200px', padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4CAF50' }}>
              Rs. {totalEarnings.toLocaleString()}
            </div>
            <div style={{ color: '#666', marginTop: '5px' }}>Total Earnings</div>
          </div>
          <div className="card" style={{ flex: 1, minWidth: '200px', padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2196F3' }}>
              {totalOrders}
            </div>
            <div style={{ color: '#666', marginTop: '5px' }}>Total Deliveries</div>
          </div>
          <div className="card" style={{ flex: 1, minWidth: '200px', padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#FF9800' }}>
              {earnings.length}
            </div>
            <div style={{ color: '#666', marginTop: '5px' }}>Total Riders</div>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span className="alert-text">{error}</span>
          </div>
        )}

        {/* Earnings Table */}
        {earnings.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💰</div>
            <h2 className="empty-state-title">No earnings found</h2>
            <p className="empty-state-text">No delivered orders in selected date range</p>
          </div>
        ) : (
          <div className="products-table-container">
            <table className="products-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Rider Name</th>
                  <th>Email</th>
                  <th>Total Deliveries</th>
                  <th>Total Earnings</th>
                  <th>Avg per Order</th>
                  <th>First Delivery</th>
                  <th>Last Delivery</th>
                </tr>
              </thead>
              <tbody>
                {earnings.map((rider, index) => (
                  <tr key={rider.rider_id}>
                    <td>{index + 1}</td>
                    <td>
                      <div style={{ fontWeight: '600' }}>🛵 {rider.rider_name}</div>
                      <div style={{ fontSize: '12px', color: '#888' }}>ID: {rider.rider_id}</div>
                    </td>
                    <td>{rider.rider_email || '—'}</td>
                    <td>
                      <span className="stock-badge">
                        {rider.total_orders || 0} orders
                      </span>
                    </td>
                    <td>
                      <span style={{ fontWeight: '700', color: '#4CAF50', fontSize: '1rem' }}>
                        Rs. {Number(rider.total_earnings || 0).toLocaleString()}
                      </span>
                    </td>
                    <td>
                      Rs. {rider.total_orders > 0
                        ? Math.round(rider.total_earnings / rider.total_orders).toLocaleString()
                        : '—'}
                    </td>
                    <td>
                      {rider.first_delivery
                        ? new Date(rider.first_delivery).toLocaleDateString('en-PK')
                        : '—'}
                    </td>
                    <td>
                      {rider.last_delivery
                        ? new Date(rider.last_delivery).toLocaleDateString('en-PK')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </div>
  );
}